import { Button } from '@/shared/components/ui/Button';
import { useClientPagination } from '@/shared/hooks/useClientPagination';
import { useEscapeClose } from '@/shared/hooks/useEscapeClose';
import { ApiError } from '@/shared/lib/api-client';
import { confirm } from '@/shared/lib/confirm';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Edit2, Loader2, Lock, Plus, Search, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  type QuickReplyRow,
  createQuickReply,
  deleteQuickReply,
  listAccessGroups,
  listQuickRepliesManage,
  updateQuickReply,
} from '../api';

/** Regex do backend pra atalho (vide crud-routes.ts: `/^\/[\w-]+$/`). */
const SHORTCUT_REGEX = /^\/[\w-]+$/;
const SHORTCUT_MAX = 40;
const BODY_MAX = 4000;

/** Iniciais do nome pro avatar (1ª letra do 1º e do último nome). */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Cor estável do avatar por id do atendente (hash simples → paleta fixa). */
const AVATAR_COLORS = ['#0A4EE3', '#059669', '#b45309', '#7c3aed', '#dc2626', '#0891b2', '#db2777'];
function avatarColor(id: number): string {
  return AVATAR_COLORS[id % AVATAR_COLORS.length] as string;
}

function HighlightedMessage({ text }: { text: string }) {
  const parts = text.split(/(\{\{[^}]+\}\})/g);
  return (
    <>
      {parts.map((part, i) =>
        /^\{\{[^}]+\}\}$/.test(part) ? (
          <code
            key={i}
            style={{
              background: 'var(--warning-100)',
              color: 'var(--warning-700)',
              padding: '1px 5px',
              borderRadius: '3px',
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
            }}
          >
            {part}
          </code>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

export function QuickRepliesTab() {
  const qc = useQueryClient();
  // Lista de GESTÃO (todas as mensagens, com grupos de acesso) — diferente da
  // lista FILTRADA por membership que o composer consome.
  const q = useQuery({ queryKey: ['quick-replies-manage'], queryFn: listQuickRepliesManage });
  const [search, setSearch] = useState('');
  // Filtro "Criado por": 'empresa' (padrão) | 'todos' | id do atendente.
  const [owner, setOwner] = useState<'empresa' | 'todos' | number>('empresa');
  const [editing, setEditing] = useState<QuickReplyRow | 'new' | null>(null);
  const delMut = useMutation({
    mutationFn: deleteQuickReply,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quick-replies-manage'] }),
  });

  // Atendentes distintos que têm pessoais — popula o dropdown "Criado por".
  const owners = useMemo(() => {
    const map = new Map<number, string>();
    for (const r of q.data ?? []) if (r.created_by) map.set(r.created_by.id, r.created_by.name);
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [q.data]);

  const filtered = useMemo(() => {
    const term = search.toLowerCase();
    return (q.data ?? []).filter((r) => {
      const matchOwner =
        owner === 'todos'
          ? true
          : owner === 'empresa'
            ? r.created_by === null
            : r.created_by?.id === owner;
      const matchSearch =
        r.shortcut.toLowerCase().includes(term) || r.body.toLowerCase().includes(term);
      return matchOwner && matchSearch;
    });
  }, [q.data, search, owner]);

  const { visible, hasMore, sentinelRef, resetPagination } = useClientPagination(filtered);

  // Reseta paginação quando a busca muda — senão visibleCount cresce demais e
  // primeiro resultado da nova busca sai do viewport.
  useEffect(() => {
    resetPagination();
  }, [search, owner, resetPagination]);

  return (
    <section className="flex h-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0" style={{ marginBottom: '20px' }}>
        <h2
          style={{
            fontSize: '20px',
            fontWeight: 700,
            color: 'var(--n-900)',
            letterSpacing: '-.02em',
            marginBottom: '4px',
          }}
        >
          Mensagens Rápidas
        </h2>
        <p style={{ fontSize: '13px', color: 'var(--n-500)' }}>
          Crie atalhos pra inserir mensagens prontas no chat com{' '}
          <code
            style={{
              background: 'var(--n-100)',
              padding: '1px 5px',
              borderRadius: '3px',
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
            }}
          >
            /atalho
          </code>
          . Suporta variáveis como{' '}
          <code
            style={{
              background: 'var(--warning-100)',
              color: 'var(--warning-700)',
              padding: '1px 5px',
              borderRadius: '3px',
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
            }}
          >
            {'{{firstname}}'}
          </code>{' '}
          (nome do cliente) e{' '}
          <code
            style={{
              background: 'var(--warning-100)',
              color: 'var(--warning-700)',
              padding: '1px 5px',
              borderRadius: '3px',
              fontFamily: 'var(--font-mono)',
              fontSize: '11px',
            }}
          >
            {'{{atendente}}'}
          </code>{' '}
          (seu nome).
        </p>
      </div>

      {/* Toolbar */}
      <div
        className="flex-shrink-0"
        style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            background: 'var(--bg-surface)',
            border: '1px solid var(--n-300)',
            borderRadius: 'var(--r-md)',
            height: '36px',
            padding: '0 12px',
            flex: 1,
          }}
        >
          <Search size={14} strokeWidth={1.75} style={{ color: 'var(--n-400)', flexShrink: 0 }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar mensagem rápida…"
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              background: 'transparent',
              fontFamily: 'inherit',
              fontSize: '13px',
              color: 'var(--n-900)',
            }}
          />
        </div>
        {/* Filtro "Criado por": empresa (padrão) / todos / cada atendente com
            pessoais. Client-side — a lista de gestão já vem completa. */}
        <select
          value={typeof owner === 'number' ? String(owner) : owner}
          onChange={(e) => {
            const v = e.target.value;
            setOwner(v === 'empresa' || v === 'todos' ? v : Number(v));
          }}
          title="Filtrar por quem criou"
          className="h-[36px] rounded-md border border-n-300 bg-n-0 px-3 text-[13px] text-n-800 outline-none focus:border-brand-500"
        >
          <option value="empresa">Da empresa</option>
          <option value="todos">Todos</option>
          {owners.length > 0 && <option disabled>──────────</option>}
          {owners.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
            </option>
          ))}
        </select>
        <Button onClick={() => setEditing('new')}>
          <Plus aria-hidden="true" />
          Nova mensagem rápida
        </Button>
      </div>

      {/* Contagem */}
      {!q.isLoading && filtered.length > 0 && (
        <p
          className="flex-shrink-0"
          style={{ fontSize: '12px', color: 'var(--n-500)', marginBottom: '8px' }}
        >
          {visible.length === filtered.length
            ? `${filtered.length} ${filtered.length === 1 ? 'mensagem' : 'mensagens'}`
            : `Mostrando ${visible.length} de ${filtered.length} mensagens`}
        </p>
      )}

      {/* List — table semântica + viewport adaptativo via flex chain
          (estilo /contatos). flex-1 + min-h-0 + overflow-y-auto faz o card
          ocupar todo espaço vertical restante e scrollar quando precisa. */}
      <div className="min-h-0 flex flex-col overflow-y-auto rounded-md border border-n-200 bg-n-0 shadow-sm">
        <table className="w-full text-[12px]">
          <thead className="sticky top-0 z-10 border-b border-n-200 bg-n-50 text-left">
            <tr>
              <th className="w-[160px] px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Atalho
              </th>
              <th className="px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Mensagem
              </th>
              <th className="w-[170px] px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Criado por
              </th>
              <th className="w-[130px] px-[14px] py-[10px] font-bold text-[11px] uppercase text-n-500">
                Acesso
              </th>
              <th className="w-[80px] px-[14px] py-[10px] text-right font-bold text-[11px] uppercase text-n-500">
                Ações
              </th>
            </tr>
          </thead>
          <tbody>
            {q.isLoading && (
              <tr>
                <td colSpan={5} className="px-3 py-8 text-center">
                  <Loader2 className="mx-auto animate-spin text-n-400" />
                </td>
              </tr>
            )}
            {!q.isLoading && filtered.length === 0 && (
              <tr>
                <td colSpan={5} className="px-3 py-12 text-center text-n-500">
                  Nenhuma mensagem rápida encontrada.
                </td>
              </tr>
            )}
            {visible.map((r) => (
              <tr key={r.id} className="border-b border-n-100 last:border-b-0 hover:bg-n-50">
                <td className="px-[14px] py-[11px]">
                  <span className="inline-block max-w-full truncate rounded-sm bg-brand-50 px-2.5 py-1 text-center font-mono font-semibold text-[12px] text-brand-700">
                    {r.shortcut}
                  </span>
                </td>
                <td className="px-[14px] py-[11px]">
                  <span
                    title={r.body}
                    className="block text-[13px] text-n-700"
                    style={{
                      lineHeight: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      wordBreak: 'break-word',
                    }}
                  >
                    <HighlightedMessage text={r.body} />
                  </span>
                </td>
                <td className="px-[14px] py-[11px]">
                  {r.created_by === null ? (
                    <span className="inline-flex items-center gap-1.5 rounded-pill bg-brand-50 px-2.5 py-[3px] font-bold text-[11px] text-brand-700">
                      <span className="h-1.5 w-1.5 rounded-full bg-brand-500" />
                      Empresa
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full font-bold text-[10px] text-white"
                        style={{ background: avatarColor(r.created_by.id) }}
                      >
                        {initials(r.created_by.name)}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate font-semibold text-[12px] text-n-700">
                          {r.created_by.name}
                        </span>
                        <span className="block text-[10px] font-semibold uppercase tracking-wide text-n-400">
                          pessoal
                        </span>
                      </span>
                    </span>
                  )}
                </td>
                <td className="px-[14px] py-[11px]">
                  {r.created_by !== null ? (
                    <span className="text-[11px] text-n-400">—</span>
                  ) : r.access_group_ids.length > 0 ? (
                    <span
                      className="inline-flex items-center gap-1 rounded-pill bg-brand-50 px-2 py-[2px] font-semibold text-[11px] text-brand-700"
                      title="Visível só pra membros dos grupos selecionados"
                    >
                      <Lock size={11} strokeWidth={2} />
                      {r.access_group_ids.length}{' '}
                      {r.access_group_ids.length === 1 ? 'grupo' : 'grupos'}
                    </span>
                  ) : (
                    <span className="text-[11px] text-n-400">Todos</span>
                  )}
                </td>
                <td className="px-[14px] py-[11px]">
                  <div className="flex justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => setEditing(r)}
                      title="Editar"
                      className="flex h-[30px] w-[30px] items-center justify-center rounded-sm text-n-500 hover:bg-n-100 hover:text-n-800"
                    >
                      <Edit2 size={14} strokeWidth={1.75} />
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        const ok = await confirm({
                          title: `Apagar "${r.shortcut}"?`,
                          confirmText: 'Apagar',
                          variant: 'danger',
                        });
                        if (ok) delMut.mutate(r.id);
                      }}
                      title="Apagar"
                      className="flex h-[30px] w-[30px] items-center justify-center rounded-sm text-n-500 hover:bg-danger-50 hover:text-danger-600"
                    >
                      <Trash2 size={14} strokeWidth={1.75} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {hasMore && (
              <tr ref={sentinelRef}>
                <td colSpan={5} className="py-4 text-center text-[12px] text-n-500">
                  <span className="inline-flex items-center gap-2">
                    <Loader2 size={14} className="animate-spin" /> Carregando mais…
                  </span>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Tip */}
      <div
        className="flex-shrink-0"
        style={{ marginTop: '14px', fontSize: '12px', color: 'var(--n-500)' }}
      >
        💡 <b>Dica:</b> no composer do atendimento, digite{' '}
        <code
          style={{
            background: 'var(--n-100)',
            padding: '1px 5px',
            borderRadius: '3px',
            fontFamily: 'var(--font-mono)',
            fontSize: '11px',
          }}
        >
          /
        </code>{' '}
        pra abrir a lista de mensagens rápidas inline.
      </div>

      {editing && (
        <QuickReplyFormModal
          quick={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </section>
  );
}

function QuickReplyFormModal({
  quick,
  onClose,
}: {
  quick: QuickReplyRow | null;
  onClose: () => void;
}) {
  useEscapeClose(true, onClose);
  const qc = useQueryClient();
  // Editando uma PESSOAL de atendente (moderação): sem grupos de acesso —
  // pessoal é privada. Criar (quick=null) é sempre da empresa → com grupos.
  const isPersonal = quick !== null && quick.created_by !== null;
  const [shortcut, setShortcut] = useState(quick?.shortcut ?? '/');
  const [body, setBody] = useState(quick?.body ?? '');
  const [groupIds, setGroupIds] = useState<number[]>(quick?.access_group_ids ?? []);
  const groupsQ = useQuery({
    queryKey: ['access-groups'],
    queryFn: listAccessGroups,
    enabled: !isPersonal,
  });
  // Erros de validação client-side por campo. Aparecem em vermelho abaixo
  // do input quando o usuário tenta salvar com valor inválido.
  const [shortcutError, setShortcutError] = useState<string | null>(null);
  const [bodyError, setBodyError] = useState<string | null>(null);

  function validate(): boolean {
    let ok = true;
    setShortcutError(null);
    setBodyError(null);
    if (shortcut.length < 2) {
      setShortcutError('Atalho precisa ter pelo menos 2 caracteres.');
      ok = false;
    } else if (shortcut.length > SHORTCUT_MAX) {
      setShortcutError(`Atalho não pode passar de ${SHORTCUT_MAX} caracteres.`);
      ok = false;
    } else if (!SHORTCUT_REGEX.test(shortcut)) {
      setShortcutError(
        'Atalho deve começar com / e usar só letras, números, _ e - (sem espaços ou acentos).',
      );
      ok = false;
    }
    if (!body.trim()) {
      setBodyError('Mensagem não pode ficar vazia.');
      ok = false;
    } else if (body.length > BODY_MAX) {
      setBodyError(`Mensagem não pode passar de ${BODY_MAX} caracteres.`);
      ok = false;
    }
    return ok;
  }

  const mut = useMutation({
    mutationFn: async () => {
      // Pessoal: não envia grupos (backend ignora, mas mantemos o payload limpo).
      if (quick)
        await updateQuickReply(quick.id, {
          shortcut,
          body,
          ...(isPersonal ? {} : { access_group_ids: groupIds }),
        });
      else await createQuickReply({ shortcut, body, access_group_ids: groupIds });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['quick-replies-manage'] });
      onClose();
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        // 409: atalho já existe no tenant. Mostra inline no campo.
        if (err.problem.type?.endsWith('QUICK_REPLY_SHORTCUT_TAKEN') || err.status === 409) {
          setShortcutError('Esse atalho já existe. Escolha outro.');
          return;
        }
        toast.error(err.problem.title, { description: err.problem.detail });
      } else {
        toast.error('Erro ao salvar', { description: String(err) });
      }
    },
  });

  function handleSave(): void {
    if (!validate()) return;
    mut.mutate();
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-[520px] rounded-lg bg-n-0 p-6 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-[16px]">
            {quick ? 'Editar mensagem' : 'Nova mensagem rápida'}
          </h3>
          <button type="button" onClick={onClose} className="text-n-500" aria-label="Fechar">
            <X size={16} />
          </button>
        </div>
        <div className="mt-4 flex flex-col gap-3">
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Atalho (começa com /)</span>
            <input
              value={shortcut}
              onChange={(e) => {
                setShortcut(e.target.value);
                if (shortcutError) setShortcutError(null);
              }}
              maxLength={SHORTCUT_MAX}
              className={`rounded-md border px-3 py-2 font-mono text-[13px] ${
                shortcutError ? 'border-danger-500' : 'border-n-200'
              }`}
              placeholder="/saudacao"
            />
            <div className="flex justify-between text-[11px]">
              <span className={shortcutError ? 'text-danger-600' : 'text-n-500'}>
                {shortcutError ?? 'Sem espaços ou acentos. Apenas letras, números, _ e -.'}
              </span>
              <span className="text-n-400">
                {shortcut.length}/{SHORTCUT_MAX}
              </span>
            </div>
          </label>
          <label className="flex flex-col gap-1">
            <span className="font-semibold text-[12px] text-n-700">Mensagem</span>
            <textarea
              value={body}
              onChange={(e) => {
                setBody(e.target.value);
                if (bodyError) setBodyError(null);
              }}
              maxLength={BODY_MAX}
              className={`min-h-[120px] resize-y rounded-md border px-3 py-2 text-[13px] ${
                bodyError ? 'border-danger-500' : 'border-n-200'
              }`}
              placeholder="Olá {{firstname}}! Tudo bem?"
            />
            <div className="flex justify-between text-[11px]">
              <span className={bodyError ? 'text-danger-600' : 'text-n-500'}>
                {bodyError ??
                  'Use {{firstname}}, {{name}}, {{phone}}, {{email}}, {{state_uf}}, {{custom.X}}.'}
              </span>
              <span className="text-n-400">
                {body.length}/{BODY_MAX}
              </span>
            </div>
          </label>
          {isPersonal ? (
            <div className="rounded-md border border-n-200 bg-n-50 px-3 py-2 text-[12px] text-n-600">
              Mensagem <b>pessoal</b> de um atendente — visível só pra ele. Você está editando como
              moderador.
            </div>
          ) : (
            <label className="flex flex-col gap-1">
              <span className="font-semibold text-[12px] text-n-700">Grupos de acesso</span>
              <div className="flex flex-wrap gap-1.5">
                {groupsQ.isLoading && (
                  <span className="text-[12px] text-n-500">Carregando grupos…</span>
                )}
                {!groupsQ.isLoading && (groupsQ.data?.length ?? 0) === 0 && (
                  <span className="text-[12px] text-n-500">
                    Nenhum grupo cadastrado. Crie em “Grupos de Acesso”.
                  </span>
                )}
                {(groupsQ.data ?? []).map((g) => {
                  const active = groupIds.includes(g.id);
                  return (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() =>
                        setGroupIds((prev) =>
                          active ? prev.filter((id) => id !== g.id) : [...prev, g.id],
                        )
                      }
                      className={`rounded-pill border px-2.5 py-1 text-[11px] font-semibold transition ${
                        active
                          ? 'border-transparent bg-brand-500 text-white'
                          : 'border-n-200 bg-n-0 text-n-700 hover:bg-n-50'
                      }`}
                    >
                      {g.name}
                    </button>
                  );
                })}
              </div>
              <span className="text-[11px] text-n-500">
                Vazio = todos veem. Com grupo(s), só membros veem.
              </span>
            </label>
          )}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleSave} disabled={mut.isPending || !body.trim()}>
            {mut.isPending && <Loader2 size={12} className="animate-spin" />}
            Salvar
          </Button>
        </div>
      </div>
    </div>
  );
}
